import { useEffect, useRef, useState } from 'react'
import { CAMADA_DA_CHAMADA } from './camadas.ts'
import { AnchoredOverlay, useToast } from '@orbind/ui'
import { aparelhoEscolhido, useAparelhos, type TipoDeAparelho } from './dispositivos.ts'
import { guardarImagemDeFundo, type EstiloDeFundo } from './fundo.ts'

/**
 * Controles de mídia compartilhados entre a ANTESSALA e a chamada em andamento
 * (SPEC-20260917-0047).
 *
 * Os dois lugares oferecem a mesma coisa — escolher microfone, câmera, saída de
 * som e fundo —, então os menus moram aqui em vez de duplicados. O que a
 * antessala acrescenta é o TESTE: ver a barra do microfone reagir à própria voz
 * e ouvir um som sair pela caixa escolhida, antes de alguém atender.
 */

/* ------------------------------------------------ teste do microfone --- */

/**
 * O quanto o microfone está captando AGORA, de 0 a 1.
 *
 * Serve para a pessoa ver que o aparelho escolhido é mesmo o que está pegando a
 * voz dela — um microfone mudo (cabo solto, tecla de mudo na própria peça) fica
 * evidente aqui, e não depois que o outro lado atende.
 */
export function useNivelDoMicrofone(faixa: MediaStreamTrack | null, ativo = true): number {
  const [nivel, setNivel] = useState(0)

  useEffect(() => {
    if (!faixa || !ativo) {
      setNivel(0)
      return
    }
    let vivo = true
    let ctx: AudioContext | null = null
    let quadro = 0
    try {
      ctx = new AudioContext()
      void ctx.resume().catch(() => undefined)
      const analisador = ctx.createAnalyser()
      analisador.fftSize = 512
      ctx.createMediaStreamSource(new MediaStream([faixa])).connect(analisador)
      const amostras = new Uint8Array(analisador.fftSize)
      const medir = () => {
        if (!vivo) return
        analisador.getByteTimeDomainData(amostras)
        // Média quadrática: o quanto a onda se afasta do silêncio (128).
        let soma = 0
        for (const v of amostras) soma += (v - 128) * (v - 128)
        const rms = Math.sqrt(soma / amostras.length) / 128
        // A escala do ouvido não é linear; a raiz deixa a fala mover a barra.
        setNivel(Math.min(1, Math.sqrt(rms) * 1.8))
        quadro = requestAnimationFrame(medir)
      }
      quadro = requestAnimationFrame(medir)
    } catch {
      // Sem Web Audio: a barra fica parada, o resto da escolha continua valendo.
    }
    return () => {
      vivo = false
      cancelAnimationFrame(quadro)
      void ctx?.close().catch(() => undefined)
    }
  }, [faixa, ativo])

  return nivel
}

/** Barra de nível — acesa proporcional ao que o microfone capta. */
export function BarraDeNivel({ nivel, rotulo }: { nivel: number; rotulo?: string }) {
  return (
    <div
      className="vc-nivel"
      role="meter"
      aria-label={rotulo ?? 'Nível do microfone'}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(nivel * 100)}
    >
      <span className="vc-nivel__barra" style={{ width: `${Math.round(nivel * 100)}%` }} />
    </div>
  )
}

/**
 * Toca um som curto na saída ESCOLHIDA, para a pessoa confirmar que o som sai
 * onde ela espera (fone, caixa, monitor). O tom é gerado aqui — nada baixado.
 */
export async function tocarSomDeTeste(): Promise<void> {
  const ctx = new AudioContext()
  try {
    await ctx.resume().catch(() => undefined)
    const destino = ctx.createMediaStreamDestination()
    const oscilador = ctx.createOscillator()
    const ganho = ctx.createGain()
    oscilador.type = 'sine'
    oscilador.frequency.value = 660
    // Sobe e desce em rampa: um tom que começa seco estala no alto-falante.
    const agora = ctx.currentTime
    ganho.gain.setValueAtTime(0, agora)
    ganho.gain.linearRampToValueAtTime(0.25, agora + 0.05)
    ganho.gain.setValueAtTime(0.25, agora + 0.35)
    ganho.gain.linearRampToValueAtTime(0, agora + 0.5)
    oscilador.connect(ganho).connect(destino)
    oscilador.start()
    oscilador.stop(agora + 0.5)

    // O elemento é quem sabe respeitar a saída escolhida (`setSinkId`).
    const audio = document.createElement('audio')
    audio.srcObject = destino.stream
    const id = aparelhoEscolhido('saida')
    const comSink = audio as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }
    if (id && typeof comSink.setSinkId === 'function') await comSink.setSinkId(id).catch(() => undefined)
    await audio.play().catch(() => undefined)
    await new Promise((pronto) => setTimeout(pronto, 600))
    audio.srcObject = null
  } finally {
    await ctx.close().catch(() => undefined)
  }
}

/* ------------------------------------------------------- os menus --- */

/**
 * Menu de aparelhos (SPEC-20260916-2346): microfone, câmera e caixa de som, com
 * a escolha guardada no computador. A lista só mostra nomes de verdade depois da
 * permissão — antes disso o navegador esconde os rótulos.
 *
 * Com `faixaDoMicrofone`, o menu também TESTA (SPEC-20260917-0047): a barra
 * reage à voz e o botão manda um som pela saída escolhida.
 */
export function MenuDeAparelhos({
  ancora,
  onFechar,
  onTrocarMicrofone,
  onTrocarCamera,
  onTrocarSaida,
  faixaDoMicrofone = null,
  comTeste = false,
}: {
  ancora: HTMLElement
  onFechar: () => void
  onTrocarMicrofone: (id: string) => void
  onTrocarCamera: (id: string) => void
  onTrocarSaida: () => void
  faixaDoMicrofone?: MediaStreamTrack | null
  comTeste?: boolean
}) {
  const microfones = useAparelhos('microfone')
  const cameras = useAparelhos('camera')
  const saidas = useAparelhos('saida')
  const temSaida =
    typeof (document.createElement('video') as HTMLVideoElement & { setSinkId?: unknown }).setSinkId === 'function'
  const nivel = useNivelDoMicrofone(faixaDoMicrofone, comTeste)
  const [tocando, setTocando] = useState(false)

  const grupo = (
    titulo: string,
    icone: string,
    tipo: TipoDeAparelho,
    dados: ReturnType<typeof useAparelhos>,
    aoTrocar: (id: string) => void,
    extra?: React.ReactNode,
  ) => (
    <section className="vc-aparelhos__grupo" key={tipo}>
      <h3 className="vc-aparelhos__titulo">
        <i className={icone} aria-hidden /> {titulo}
      </h3>
      {dados.lista.length === 0 ? (
        <p className="vc-aparelhos__vazio">Nenhum encontrado.</p>
      ) : (
        <ul className="vc-aparelhos__lista" role="listbox" aria-label={titulo}>
          {dados.lista.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                role="option"
                aria-selected={dados.escolhido === a.id}
                className={`vc-aparelhos__item${dados.escolhido === a.id ? ' vc-aparelhos__item--on' : ''}`}
                onClick={() => {
                  dados.escolher(a.id)
                  aoTrocar(a.id)
                }}
              >
                <i className={dados.escolhido === a.id ? 'fas fa-check' : 'fas fa-circle'} aria-hidden />
                <span>{a.nome}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {extra}
    </section>
  )

  const testeDoMicrofone = comTeste ? (
    <div className="vc-aparelhos__teste">
      <BarraDeNivel nivel={nivel} />
      <span className="vc-aparelhos__dica">
        {faixaDoMicrofone ? 'Fale para ver a barra se mexer.' : 'Ligue o microfone para testar.'}
      </span>
    </div>
  ) : undefined

  const testeDoSom = comTeste ? (
    <button
      type="button"
      className="vc-aparelhos__botao"
      disabled={tocando}
      onClick={() => {
        setTocando(true)
        void tocarSomDeTeste().finally(() => setTocando(false))
      }}
    >
      <i className={tocando ? 'fas fa-volume-up' : 'fas fa-play'} aria-hidden />{' '}
      {tocando ? 'Tocando…' : 'Testar o som'}
    </button>
  ) : undefined

  return (
    <AnchoredOverlay
      anchor={ancora}
      onClose={onFechar}
      prefer="acima"
      gap={14}
      role="dialog"
      aria-label="Aparelhos da chamada"
      className="vc-aparelhos"
          zIndex={CAMADA_DA_CHAMADA}
    >
      {grupo('Microfone', 'fas fa-microphone', 'microfone', microfones, onTrocarMicrofone, testeDoMicrofone)}
      {grupo('Câmera', 'fas fa-video', 'camera', cameras, onTrocarCamera)}
      {temSaida ? grupo('Som sai por', 'fas fa-volume-up', 'saida', saidas, onTrocarSaida, testeDoSom) : null}
    </AnchoredOverlay>
  )
}

/**
 * A lista de UM tipo de aparelho, aberta pela setinha ao lado do próprio botão
 * (pedido do usuário, 2026-09-17 23:47, com a barra do Meet como referência).
 *
 * É melhor que um botão único de "aparelhos": quem quer trocar de microfone
 * procura o microfone, não uma engrenagem genérica. O teste fica no rodapé da
 * lista, junto do que ele testa.
 */
export function MenuDeUmAparelho({
  ancora,
  titulo,
  icone,
  tipo,
  onFechar,
  onTrocar,
  rodape,
}: {
  ancora: HTMLElement
  titulo: string
  icone: string
  tipo: TipoDeAparelho
  onFechar: () => void
  onTrocar: (id: string) => void
  rodape?: React.ReactNode
}) {
  const aparelhos = useAparelhos(tipo)

  return (
    <AnchoredOverlay
      anchor={ancora}
      onClose={onFechar}
      prefer="acima"
      gap={12}
      role="dialog"
      aria-label={titulo}
      className="vc-aparelhos vc-aparelhos--um"
    >
      <h3 className="vc-aparelhos__titulo">
        <i className={icone} aria-hidden /> {titulo}
      </h3>
      {aparelhos.lista.length === 0 ? (
        <p className="vc-aparelhos__vazio">Nenhum encontrado.</p>
      ) : (
        <ul className="vc-aparelhos__lista" role="listbox" aria-label={titulo}>
          {aparelhos.lista.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                role="option"
                aria-selected={aparelhos.escolhido === a.id}
                className={`vc-aparelhos__item${aparelhos.escolhido === a.id ? ' vc-aparelhos__item--on' : ''}`}
                onClick={() => {
                  aparelhos.escolher(a.id)
                  onTrocar(a.id)
                  onFechar()
                }}
              >
                <i className={aparelhos.escolhido === a.id ? 'fas fa-check' : 'fas fa-circle'} aria-hidden />
                <span>{a.nome}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {rodape}
    </AnchoredOverlay>
  )
}

/**
 * Chip com o NOME do aparelho em uso e a seta que abre a lista.
 *
 * É o elemento que o usuário apontou na barra do Meet (2026-09-17 23:52): não
 * basta a setinha, o nome precisa estar à vista. Saber que está no "Viva voz
 * (Brio 500)" e não no microfone da tela é metade do problema de quem entra
 * numa reunião e ninguém ouve.
 */
export function ChipDeAparelho({
  tipo,
  icone,
  titulo,
  onTrocar,
  rodape,
}: {
  tipo: TipoDeAparelho
  icone: string
  titulo: string
  onTrocar: (id: string) => void
  rodape?: React.ReactNode
}) {
  const [menu, setMenu] = useState<HTMLElement | null>(null)
  const aparelhos = useAparelhos(tipo)
  const atual = aparelhos.lista.find((a) => a.id === aparelhos.escolhido) ?? aparelhos.lista[0]

  return (
    <>
      <button
        type="button"
        className="vc-chip"
        aria-haspopup="dialog"
        aria-expanded={menu !== null}
        aria-label={`${titulo}: ${atual?.nome ?? 'padrão do sistema'}. Escolher outro`}
        title={atual?.nome ?? titulo}
        onClick={(e) => {
          const alvo = e.currentTarget
          setMenu((aberto) => (aberto ? null : alvo))
        }}
      >
        <i className={icone} aria-hidden />
        <span className="vc-chip__nome">{atual?.nome ?? titulo}</span>
        <i className={`fas fa-chevron-${menu ? 'up' : 'down'} vc-chip__seta`} aria-hidden />
      </button>
      {menu ? (
        <MenuDeUmAparelho
          ancora={menu}
          titulo={titulo}
          icone={icone}
          tipo={tipo}
          onFechar={() => setMenu(null)}
          onTrocar={onTrocar}
          rodape={rodape}
        />
      ) : null}
    </>
  )
}

/** Barra de nível do microfone, para o rodapé da lista de microfones. */
export function TesteDoMicrofone({ faixa, ativo }: { faixa: MediaStreamTrack | null; ativo: boolean }) {
  const nivel = useNivelDoMicrofone(faixa, ativo)
  return (
    <div className="vc-aparelhos__teste">
      <BarraDeNivel nivel={nivel} />
      <span className="vc-aparelhos__dica">{faixa && ativo ? 'Fale para ver a barra se mexer.' : 'Ligue o microfone para testar.'}</span>
    </div>
  )
}

/** Botão que toca um som na saída escolhida, para o rodapé da lista de saídas. */
export function TesteDoSom() {
  const [tocando, setTocando] = useState(false)
  return (
    <button
      type="button"
      className="vc-aparelhos__botao"
      disabled={tocando}
      onClick={() => {
        setTocando(true)
        void tocarSomDeTeste().finally(() => setTocando(false))
      }}
    >
      <i className={tocando ? 'fas fa-volume-up' : 'fas fa-play'} aria-hidden /> {tocando ? 'Tocando…' : 'Testar o som'}
    </button>
  )
}

/**
 * Menu do fundo da câmera (SPEC-20260916-2346): sem fundo, desfocado ou uma
 * imagem sua. A imagem fica guardada neste computador, reduzida.
 */
export function MenuDeFundo({
  ancora,
  estilo,
  imagem,
  onFechar,
  onTrocar,
}: {
  ancora: HTMLElement
  estilo: EstiloDeFundo
  imagem: string | null
  onFechar: () => void
  onTrocar: (estilo: EstiloDeFundo, imagem?: string | null) => void
}) {
  const arquivo = useRef<HTMLInputElement>(null)
  const { push } = useToast()

  const escolherArquivo = async (f: File | undefined) => {
    if (!f) return
    try {
      const dataUrl = await guardarImagemDeFundo(f)
      onTrocar('imagem', dataUrl)
    } catch (e) {
      push({ tone: 'danger', message: e instanceof Error ? e.message : 'Não foi possível usar essa imagem.' })
    }
  }

  return (
    <AnchoredOverlay
      anchor={ancora}
      onClose={onFechar}
      prefer="acima"
      gap={14}
      role="dialog"
      aria-label="Fundo da câmera"
      className="vc-fundo"
          zIndex={CAMADA_DA_CHAMADA}
    >
      <h3 className="vc-aparelhos__titulo">
        <i className="fas fa-image" aria-hidden /> Fundo
      </h3>
      <div className="vc-fundo__opcoes">
        <button
          type="button"
          className={`vc-fundo__op${estilo === 'nenhum' ? ' vc-fundo__op--on' : ''}`}
          aria-pressed={estilo === 'nenhum'}
          onClick={() => onTrocar('nenhum')}
        >
          <span className="vc-fundo__previa vc-fundo__previa--nenhum" aria-hidden>
            <i className="fas fa-ban" />
          </span>
          Nenhum
        </button>
        <button
          type="button"
          className={`vc-fundo__op${estilo === 'desfoque' ? ' vc-fundo__op--on' : ''}`}
          aria-pressed={estilo === 'desfoque'}
          onClick={() => onTrocar('desfoque')}
        >
          <span className="vc-fundo__previa vc-fundo__previa--desfoque" aria-hidden />
          Desfocar
        </button>
        <button
          type="button"
          className={`vc-fundo__op${estilo === 'imagem' ? ' vc-fundo__op--on' : ''}`}
          aria-pressed={estilo === 'imagem'}
          disabled={!imagem}
          title={imagem ? 'Usar a imagem escolhida' : 'Escolha uma imagem primeiro'}
          onClick={() => onTrocar('imagem')}
        >
          <span
            className="vc-fundo__previa"
            aria-hidden
            style={imagem ? { backgroundImage: `url(${imagem})` } : undefined}
          >
            {imagem ? null : <i className="fas fa-image" />}
          </span>
          Imagem
        </button>
      </div>
      <button type="button" className="vc-fundo__escolher" onClick={() => arquivo.current?.click()}>
        <i className="fas fa-upload" aria-hidden /> {imagem ? 'Trocar a imagem…' : 'Escolher imagem…'}
      </button>
      <input
        ref={arquivo}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          void escolherArquivo(e.target.files?.[0])
          e.target.value = ''
        }}
      />
      <p className="vc-fundo__nota">O fundo é feito aqui no seu computador; a imagem não sai daqui.</p>
    </AnchoredOverlay>
  )
}
